import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Skeleton,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import dayjs from 'dayjs';
import { listings, offers } from '@prisma/client';
import counterAnalysisService, {
  CounterDecision as CounterDecisionData,
  CounterDecisionPath,
} from '../../api/services/counterAnalysis';
import offerRoundsService, {
  OfferRoundWithRelations,
} from '../../api/services/offerRounds';
import {
  NegotiationForm,
  NegotiationPrefill,
} from '../NegotiationForm/NegotiationForm';
import useIsMobile from '../../hooks/useIsMobile';

/**
 * Counter Decision — the buyer's surface when the seller counters.
 *
 * Shows the counter's real numbers, Reva's read of them, and three paths: accept the
 * counter, send back Reva's suggested counter, or walk away. Every figure is served by
 * `GET /counter-analysis/:offerId`; nothing is computed or guessed here.
 *
 * The buttons route into the existing round flows — `accept` and `reject` on the pending
 * round, and the standard `NegotiationForm` (pre-filled) for a counter. This surface
 * never contacts the seller directly.
 */

/** Design accents for the three paths, fixed so each path reads the same on every theme. */
const SUCCESS = '#27ae60';
const WARN = '#f39c12';
const NAVY = '#2B4570';

const PATH_ICON: Record<CounterDecisionPath['key'], React.ReactNode> = {
  accept: <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />,
  counter: <SwapHorizIcon sx={{ fontSize: 20 }} />,
  walkAway: <CloseIcon sx={{ fontSize: 20 }} />,
};

/**
 * The countdown pill, recomputed locally each minute so it stays true while the buyer
 * reads. Mirrors the labels the API produces for the same window.
 */
const timeLeftLabel = (expiresAt: string, now: number): string => {
  const msLeft = new Date(expiresAt).getTime() - now;
  if (msLeft <= 0) return 'Expired';

  const hoursLeft = Math.ceil(msLeft / 3_600_000);
  if (hoursLeft <= 1) return 'Under 1h left';
  if (hoursLeft < 48) return `${hoursLeft}h left`;
  return `${Math.ceil(hoursLeft / 24)}d left`;
};

const toPrefill = (
  decision: CounterDecisionData
): NegotiationPrefill | undefined => {
  const suggested = decision.suggestedCounter;
  if (!suggested) return undefined;

  return {
    price: suggested.price,
    ...(suggested.closingDate
      ? { closingDate: dayjs(suggested.closingDate) }
      : {}),
    earnestMoney: suggested.earnestMoney,
    concessions: suggested.concessions,
    ...(suggested.inspectionPeriod !== null
      ? { inspectionPeriod: suggested.inspectionPeriod }
      : {}),
    contingent: suggested.contingent,
    appliances: suggested.appliances,
    loan: suggested.loan,
    paymentMethod: suggested.paymentMethod,
    homeWarrantyType: suggested.homeWarrantyType,
    termiteInspection: suggested.termiteInspection,
    termiteBond: suggested.termiteBond,
    sewerInspection: suggested.sewerInspection,
    newSurvey: suggested.newSurvey,
    bagentComm: suggested.bagentComm,
    // Always the buyer's own clause. The form seeds this field from the round being
    // answered, so without it the seller's text would ride back out in the buyer's
    // counter as though they had written it.
    additionalTerms: suggested.additionalTerms ?? '',
  };
};

interface PathCardProps {
  path: CounterDecisionPath;
  accent: string;
  busy: boolean;
  onAct: () => void;
}

const PathCard: React.FC<PathCardProps> = ({ path, accent, busy, onAct }) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;

  const variant = () => {
    if (path.recommended) return 'contained' as const;
    if (path.key === 'walkAway') return 'text' as const;
    return 'outlined' as const;
  };

  return (
    <Box
      data-testid={`counter-path-${path.key}`}
      data-recommended={path.recommended ? 'true' : 'false'}
      sx={{
        p: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: path.recommended
          ? alpha(primary, 0.35)
          : theme.palette.divider,
        backgroundColor: path.recommended
          ? alpha(primary, 0.05)
          : 'transparent',
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          sx={{
            width: 40,
            height: 40,
            flexShrink: 0,
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: alpha(accent, 0.12),
            color: accent,
          }}
        >
          {PATH_ICON[path.key]}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            useFlexGap
            flexWrap="wrap"
          >
            <Typography
              variant="subtitle2"
              fontWeight={700}
              color="text.primary"
            >
              {path.title}
            </Typography>
            {path.recommended && (
              <Chip
                label="Recommended"
                size="small"
                data-testid="counter-recommended-pill"
                sx={{
                  height: 22,
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  letterSpacing: 0.4,
                  color: primary,
                  backgroundColor: alpha(primary, 0.12),
                  '& .MuiChip-label': { px: 1 },
                }}
              />
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {path.subtitle}
          </Typography>
        </Box>
      </Stack>

      <Stack spacing={0.75} sx={{ mt: 1.75 }}>
        {path.stats.map((stat) => (
          <Stack
            key={stat.label}
            direction="row"
            justifyContent="space-between"
            alignItems="baseline"
            spacing={2}
          >
            <Typography variant="caption" color="text.secondary">
              {stat.label}
            </Typography>
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.primary"
              sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
            >
              {stat.value}
            </Typography>
          </Stack>
        ))}
      </Stack>

      <Button
        fullWidth
        disableElevation
        disabled={busy}
        variant={variant()}
        onClick={onAct}
        data-testid={`counter-cta-${path.key}`}
        sx={{
          mt: 1.75,
          fontWeight: 700,
          textTransform: 'none',
          ...(path.key === 'walkAway' ? { color: 'text.secondary' } : {}),
        }}
      >
        {path.cta}
      </Button>
    </Box>
  );
};

export interface CounterDecisionProps {
  offer: offers & { currentRound?: OfferRoundWithRelations };
  listing?: listings;
  /** The seller's pending round — the counter being decided on. */
  latestRound: OfferRoundWithRelations;
  onActionComplete: () => void;
  /**
   * Called when the analysis cannot be read.
   *
   * `error` means the read failed but the round the page is holding is still believed
   * current, so the offer page can safely fall back to the plain action row.
   *
   * `conflict` means the server says this offer is no longer waiting on the buyer — the
   * round in hand is stale. Acting on it would patch an obsolete round, so the page must
   * refresh rather than offer any action.
   */
  onUnavailable?: (reason: 'conflict' | 'error', roundId: string) => void;
}

export const CounterDecision: React.FC<CounterDecisionProps> = ({
  offer,
  listing,
  latestRound,
  onActionComplete,
  onUnavailable,
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const primary = theme.palette.primary.main;

  const [decision, setDecision] = useState<CounterDecisionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [counterOpen, setCounterOpen] = useState(false);
  const [confirming, setConfirming] = useState<'accept' | 'walkAway' | null>(
    null
  );
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    counterAnalysisService
      .get(offer.id)
      .then((data) => {
        if (cancelled) return;
        // The server analysed whatever round is current now. If that is not the round
        // this page is holding, the seller has moved on and every action here would
        // target an obsolete round — hand back a conflict so the page refreshes.
        if (data.currentRoundId !== latestRound.id) {
          setUnavailable(true);
          onUnavailable?.('conflict', latestRound.id);
          return;
        }
        setDecision(data);
      })
      .catch((error) => {
        if (cancelled) return;
        setUnavailable(true);
        const status = error?.response?.status;
        onUnavailable?.(status === 409 ? 'conflict' : 'error', latestRound.id);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [offer.id, latestRound.id, onUnavailable]);

  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(tick);
  }, []);

  // Reva's "Should I accept the counter?" chip links straight here, and the card only
  // exists once its data has landed — so do the anchor scroll ourselves.
  useEffect(() => {
    if (!decision || window.location.hash !== '#counter-decision') return;
    document
      .getElementById('counter-decision')
      ?.scrollIntoView({ block: 'start' });
  }, [decision]);

  const finish = useCallback(() => {
    setBusy(false);
    setConfirming(null);
    onActionComplete();
  }, [onActionComplete]);

  // The server refuses a decision on a round that is no longer pending and current
  // (409). That is not a failure to retry — the page is stale and must refresh.
  const decide = async (action: 'accept' | 'reject') => {
    setBusy(true);
    try {
      if (action === 'accept') {
        await offerRoundsService.accept(latestRound.id);
      } else {
        await offerRoundsService.reject(latestRound.id);
      }
      finish();
    } catch (error: any) {
      console.error(`Error ${action === 'accept' ? 'accepting' : 'declining'} the counter:`, error);
      setBusy(false);
      if (error?.response?.status === 409) {
        setConfirming(null);
        setUnavailable(true);
        onUnavailable?.('conflict', latestRound.id);
      }
    }
  };

  const handleAccept = () => decide('accept');
  const handleWalkAway = () => decide('reject');

  const handleCounterSubmit = async (data: any) => {
    await offerRoundsService.create({ ...data, offerId: offer.id });
    setCounterOpen(false);
    onActionComplete();
  };

  const onPathAction = (key: CounterDecisionPath['key']) => {
    if (key === 'counter') {
      setCounterOpen(true);
      return;
    }
    setConfirming(key);
  };

  if (unavailable) return null;

  if (loading || !decision) {
    return (
      <Card
        elevation={0}
        id="counter-decision"
        data-testid="counter-decision-loading"
        sx={{
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        }}
      >
        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
          <Skeleton width={160} height={18} />
          <Skeleton width="70%" height={34} sx={{ mt: 1 }} />
          <Skeleton
            variant="rectangular"
            height={96}
            sx={{ mt: 2, borderRadius: 2 }}
          />
          <Skeleton
            variant="rectangular"
            height={150}
            sx={{ mt: 2, borderRadius: 2 }}
          />
        </CardContent>
      </Card>
    );
  }

  // The countdown keeps running while the page is open, so re-derive from the clock
  // rather than trusting the status captured when the analysis was fetched.
  const expired =
    decision.status === 'expired' ||
    new Date(decision.expiresAt).getTime() <= now;
  // A counter that lapsed while the buyer was reading still carries the live read,
  // which recommends a move that is no longer on the table. Say what happened instead.
  const lapsedWhileOpen = decision.status === 'ready' && expired;
  const readText = lapsedWhileOpen
    ? "This counter's window closed while you were looking at it, so its terms are no longer on the table."
    : decision.read;
  const pillLabel = timeLeftLabel(decision.expiresAt, now);
  const pillColor = expired ? theme.palette.error.main : WARN;
  const accents: Record<CounterDecisionPath['key'], string> = {
    accept: SUCCESS,
    counter: primary,
    walkAway: NAVY,
  };

  return (
    <>
      <Card
        elevation={0}
        id="counter-decision"
        data-testid="counter-decision"
        sx={{
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        }}
      >
        <CardContent
          sx={{
            p: isMobile ? 2 : 2.5,
            '&:last-child': { pb: isMobile ? 2 : 2.5 },
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 40,
                height: 40,
                flexShrink: 0,
                borderRadius: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: alpha(primary, 0.12),
                color: primary,
              }}
            >
              <HomeOutlinedIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  color: primary,
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  textTransform: 'uppercase',
                  lineHeight: 1.3,
                }}
              >
                Seller countered
              </Typography>
              <Typography variant="body2" fontWeight={600} color="text.primary">
                {decision.address}
                {decision.city ? `, ${decision.city}` : ''}
              </Typography>
            </Box>
          </Stack>

          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            useFlexGap
            flexWrap="wrap"
            sx={{ mt: 1.5 }}
          >
            <Typography
              data-testid="counter-price"
              sx={{
                fontSize: 28,
                fontWeight: 800,
                lineHeight: 1.1,
                color: 'text.primary',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              ${decision.counterPrice.toLocaleString('en-US')}
            </Typography>
            <Chip
              label={pillLabel}
              size="small"
              data-testid="counter-expiry-pill"
              sx={{
                height: 24,
                fontSize: '0.75rem',
                fontWeight: 700,
                color: pillColor,
                backgroundColor: alpha(pillColor, 0.12),
                '& .MuiChip-label': { px: 1.25 },
              }}
            />
          </Stack>
          <Typography
            variant="body2"
            color="text.secondary"
            data-testid="counter-subhead"
          >
            {decision.subhead}
          </Typography>

          <Box
            data-testid="counter-reva-read"
            sx={{
              mt: 2,
              p: 2,
              borderRadius: '14px',
              backgroundColor: alpha(primary, 0.06),
              border: `1px solid ${alpha(primary, 0.18)}`,
            }}
          >
            <Stack direction="row" spacing={0.75} alignItems="center">
              <AutoAwesomeIcon sx={{ fontSize: 16, color: primary }} />
              <Typography
                variant="caption"
                sx={{
                  color: primary,
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  textTransform: 'uppercase',
                }}
              >
                Reva&apos;s read
              </Typography>
            </Stack>
            <Typography
              variant="body2"
              color="text.primary"
              sx={{ mt: 0.75, lineHeight: 1.6 }}
            >
              {readText}
            </Typography>
          </Box>

          {!expired && (
            <>
              <Typography
                variant="caption"
                data-testid="counter-choose-a-path"
                sx={{
                  display: 'block',
                  mt: 2.5,
                  color: 'text.secondary',
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  textTransform: 'uppercase',
                }}
              >
                Choose a path
              </Typography>

              <Stack spacing={1.5} sx={{ mt: 1.25 }}>
                {decision.paths.map((path) => (
                  <PathCard
                    key={path.key}
                    path={path}
                    accent={accents[path.key]}
                    busy={busy}
                    onAct={() => onPathAction(path.key)}
                  />
                ))}
              </Stack>
            </>
          )}

          {expired && (
            <Typography
              variant="body2"
              color="text.secondary"
              data-testid="counter-expired-note"
              sx={{ mt: 2 }}
            >
              Homepoint can reopen the conversation with the seller — nothing
              has been sent on your behalf.
            </Typography>
          )}

          {decision.payment && (
            <Typography
              variant="caption"
              color="text.secondary"
              data-testid="counter-payment-footer"
              sx={{
                display: 'block',
                mt: 2,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {decision.payment.summary}
            </Typography>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={confirming !== null}
        onClose={() => setConfirming(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {confirming === 'accept'
            ? `Accept $${decision.counterPrice.toLocaleString('en-US')}?`
            : 'Walk away from this home?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText data-testid="counter-confirm-body">
            {confirming === 'accept'
              ? "This accepts the seller's counter exactly as written and puts the home under contract."
              : 'This declines the counter and ends the negotiation on this home. ' +
                'You can keep looking at your other matches.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setConfirming(null)}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            disabled={busy}
            data-testid="counter-confirm-submit"
            onClick={confirming === 'accept' ? handleAccept : handleWalkAway}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {confirming === 'accept' ? 'Accept counter' : 'Walk away'}
          </Button>
        </DialogActions>
      </Dialog>

      {counterOpen && (
        <NegotiationForm
          open
          mode="counter"
          isBuyer
          offer={offer}
          lastRound={latestRound}
          listing={listing}
          prefill={toPrefill(decision)}
          onClose={() => setCounterOpen(false)}
          onSubmit={handleCounterSubmit}
        />
      )}
    </>
  );
};

export default CounterDecision;
